import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import { quandoLegivel, useOrganizacoesParaAutomacao } from "../../nucleo/automacao";
import { ROTULO_DA_URGENCIA, type UrgenciaDoTicket } from "../../nucleo/tickets";
import {
  DIAS_DA_SEMANA,
  FREQUENCIAS,
  ROTULO_DA_FREQUENCIA,
  descreverAgenda,
  instanteEmBrasilia,
  useAgendamentos,
  useAlterarAgendamento,
  useCriarAgendamento,
  useExecucoesDoAgendamento,
  useRemoverAgendamento,
  type Agendamento,
  type Frequencia,
} from "../../nucleo/tickets-avancado";
import { CampoDeEscolha } from "../automacao/apoio";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../ui";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";

function Execucoes({ id }: { id: string }) {
  const execucoes = useExecucoesDoAgendamento(id, true);
  if (execucoes.isPending) return <p role="status">Carregando execuções…</p>;
  if (!execucoes.data || execucoes.data.length === 0) return <p className="nota-rodape">Ainda não abriu nenhum chamado.</p>;
  return (
    <ul className="nota-rodape" aria-label="Últimas aberturas">
      {execucoes.data.map((execucao) => (
        <li key={execucao.id}>
          {quandoLegivel(execucao.executado_em)} ·{" "}
          {execucao.erro ? `não abriu: ${execucao.erro}` : `chamado #${execucao.numero_do_ticket ?? "—"}`}
        </li>
      ))}
    </ul>
  );
}

function CartaoDoAgendamento({ item, organizacao }: { item: Agendamento; organizacao: string }) {
  const alterar = useAlterarAgendamento();
  const remover = useRemoverAgendamento();
  const [vendo, setVendo] = useState(false);
  return (
    <Cartao
      titulo={item.nome}
      data-testid={`agendamento-${item.nome}`}
      acessorio={<Chip estado={item.ativo ? "ok" : "neutro"}>{item.ativo ? "Ativo" : item.proxima_execucao_em ? "Pausado" : "Concluído"}</Chip>}
    >
      <p>
        {organizacao} · {descreverAgenda(item)}
      </p>
      <p className="nota-rodape">
        Abre “{item.assunto}” · urgência {ROTULO_DA_URGENCIA[item.urgencia].toLocaleLowerCase("pt-BR")} · próxima abertura:{" "}
        {item.ativo ? quandoLegivel(item.proxima_execucao_em) : "—"} · última: {quandoLegivel(item.ultima_execucao_em)}
      </p>
      <div className="flex flex-wrap gap-2 mt-2">
        {item.ativo || item.proxima_execucao_em ? (
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            aria-label={`${item.ativo ? "Pausar" : "Reativar"} ${item.nome}`}
            onClick={() => alterar.mutate({ id: item.id, mudancas: { ativo: !item.ativo } })}
          >
            {item.ativo ? "Pausar" : "Reativar"}
          </Botao>
        ) : null}
        <Botao variante="fantasma" tamanho="pequeno" aria-label={`Ver aberturas de ${item.nome}`} onClick={() => setVendo(!vendo)}>
          {vendo ? "Ocultar aberturas" : "Ver aberturas"}
        </Botao>
        <Botao variante="fantasma" tamanho="pequeno" aria-label={`Remover ${item.nome}`} onClick={() => remover.mutate(item.id)}>
          Remover
        </Botao>
      </div>
      {vendo ? <Execucoes id={item.id} /> : null}
      {alterar.isError ? <Aviso tom="erro" className="mt-2">{mensagemDoConsole(alterar.error)}</Aviso> : null}
      {remover.isError ? <Aviso tom="erro" className="mt-2">{mensagemDoConsole(remover.error)}</Aviso> : null}
    </Cartao>
  );
}

export function PainelDeAgendamentos() {
  const agendamentos = useAgendamentos();
  const organizacoes = useOrganizacoesParaAutomacao();
  const criar = useCriarAgendamento();
  const voltarParaHome = useVoltarParaHome();
  const [organizacaoId, setOrganizacaoId] = useState("");
  const [nome, setNome] = useState("");
  const [frequencia, setFrequencia] = useState<Frequencia>("semanal");
  const [hora, setHora] = useState("09:00");
  const [diaDaSemana, setDiaDaSemana] = useState("0");
  const [diaDoMes, setDiaDoMes] = useState("1");
  const [executarEm, setExecutarEm] = useState("");
  const [assunto, setAssunto] = useState("");
  const [descricao, setDescricao] = useState("");
  const [urgencia, setUrgencia] = useState<UrgenciaDoTicket>("media");

  const nomes = useMemo(
    () => Object.fromEntries((organizacoes.data ?? []).map((o) => [o.id, o.nome_fantasia ?? o.razao_social])),
    [organizacoes.data],
  );
  const faltaQuando = frequencia === "unica" ? !executarEm : !hora;
  const invalido = !organizacaoId || nome.trim().length < 2 || assunto.trim().length < 3 || !descricao.trim() || faltaQuando;

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    if (invalido) return;
    criar.mutate(
      {
        organizacao_id: organizacaoId,
        nome: nome.trim(),
        frequencia,
        ...(frequencia === "unica" ? { executar_em: instanteEmBrasilia(executarEm) } : { hora }),
        ...(frequencia === "semanal" ? { dia_da_semana: Number(diaDaSemana) } : {}),
        ...(frequencia === "mensal" ? { dia_do_mes: Number(diaDoMes) } : {}),
        assunto: assunto.trim(),
        descricao: descricao.trim(),
        urgencia,
      },
      {
        onSuccess: () => {
          setNome("");
          setAssunto("");
          setDescricao("");
          voltarParaHome("Abertura programada criada.");
        },
      },
    );
  }

  const falha = criar.error ?? agendamentos.error;

  return (
    <div>
      <Cartao titulo="Nova abertura programada" className="mb-4">
        <form onSubmit={submeter} data-testid="formulario-de-agendamento" noValidate>
          <CampoDeEscolha
            rotulo="Empresa"
            valor={organizacaoId}
            aoMudar={setOrganizacaoId}
            vazio="Escolha a empresa"
            opcoes={(organizacoes.data ?? []).map((o) => ({ valor: o.id, rotulo: o.nome_fantasia ?? o.razao_social }))}
          />
          <Campo rotulo="Nome da rotina" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} />
          <CampoDeEscolha
            rotulo="Repetição"
            valor={frequencia}
            aoMudar={(valor) => setFrequencia(valor as Frequencia)}
            opcoes={FREQUENCIAS.map((f) => ({ valor: f, rotulo: ROTULO_DA_FREQUENCIA[f] }))}
          />
          {frequencia === "semanal" ? (
            <CampoDeEscolha rotulo="Dia da semana" valor={diaDaSemana} aoMudar={setDiaDaSemana} opcoes={DIAS_DA_SEMANA.map((dia, i) => ({ valor: String(i), rotulo: dia }))} />
          ) : null}
          {frequencia === "mensal" ? (
            <Campo rotulo="Dia do mês" type="number" min={1} max={31} value={diaDoMes} onChange={(e) => setDiaDoMes(e.target.value)} ajuda="Em meses mais curtos, abre no último dia." />
          ) : null}
          {frequencia === "unica" ? (
            <Campo rotulo="Abrir em" type="datetime-local" value={executarEm} onChange={(e) => setExecutarEm(e.target.value)} ajuda="Horário de Brasília." />
          ) : (
            <Campo rotulo="Horário" type="time" value={hora} onChange={(e) => setHora(e.target.value)} ajuda="Horário de Brasília." />
          )}
          <Campo rotulo="Assunto do chamado" value={assunto} onChange={(e) => setAssunto(e.target.value)} maxLength={200} />
          <CampoLongo rotulo="Descrição do chamado" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
          <CampoDeEscolha
            rotulo="Urgência"
            valor={urgencia}
            aoMudar={(valor) => setUrgencia(valor as UrgenciaDoTicket)}
            opcoes={Object.entries(ROTULO_DA_URGENCIA).map(([valor, rotulo]) => ({ valor, rotulo }))}
          />
          {falha ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(falha)}</Aviso> : null}
          <Botao type="submit" disabled={invalido || criar.isPending}>{criar.isPending ? "Programando…" : "Programar abertura"}</Botao>
        </form>
      </Cartao>

      <section aria-labelledby="titulo-agendamentos">
        <h2 className="titulo-secao" id="titulo-agendamentos">Aberturas programadas</h2>
        {agendamentos.isPending ? <p role="status">Carregando agenda…</p> : null}
        {agendamentos.data && agendamentos.data.length === 0 ? <Aviso tom="marca">Nenhuma abertura programada.</Aviso> : null}
        <div className="lista-de-itens" data-testid="lista-de-agendamentos">
          {agendamentos.data?.map((item) => (
            <CartaoDoAgendamento key={item.id} item={item} organizacao={nomes[item.organizacao_id] ?? "Empresa"} />
          ))}
        </div>
      </section>
    </div>
  );
}
